import React, { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../../supabase/supabase';
import { useAuth } from '../../contexts/AuthContext';
import FloatingSidebarButton from '../../components/FloatingSidebarButton/FloatingSidebarButton';
import { FiArrowLeft } from 'react-icons/fi';
import ShareProfileModal from '../../components/ShareProfileModal/ShareProfileModal';
import { useNavigate } from 'react-router-dom';
// import { useLocation } from 'react-router-dom'; // Commented out - not used after password code removal
import { useLanguage } from "../../contexts/LanguageContext";
import './Wallet.css';

// Import flag images
import aeFlag from '../../assets/images/flags/ae_flag.png';
import usFlag from '../../assets/images/flags/us_flag.png';
import frFlag from '../../assets/images/flags/fr_flag.png';
import saudiFlag from '../../assets/images/flags/saudi_flag.webp';
import syriaFlag from '../../assets/images/flags/syria_flag.webp';
import qaFlag from '../../assets/images/flags/qa_flag.svg';
import lbFlag from '../../assets/images/flags/lb_flag.svg';

const Wallet = () => {
  const [userProfile, setUserProfile] = useState(null);

  const [currencies, setCurrencies] = useState([]);
  const [selectedCurrency, setSelectedCurrency] = useState(null);
  const [showCurrencyDropdown, setShowCurrencyDropdown] = useState(false);

  const [showBalance, setShowBalance] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showShareModal, setShowShareModal] = useState(false);

  const [userTokens, setUserTokens] = useState(0); // Wini amount

  // Wini to USD exchange rate (1 winicoin = 12 USD as specified)
  const [winiToUsdRate, setWiniToUsdRate] = useState(12); // Default to 12 USD per winicoin

  // ========== COMMENTED OUT: Wallet password protection states ==========
  // const [showPasswordModal, setShowPasswordModal] = useState(false);
  // const [isPasswordSetup, setIsPasswordSetup] = useState(false);
  // const [password, setPassword] = useState('');
  // const [confirmPassword, setConfirmPassword] = useState('');
  // const [passwordError, setPasswordError] = useState('');
  // const [isPasswordVerified, setIsPasswordVerified] = useState(false);
  // const inactivityTimerRef = useRef(null);
  // const INACTIVITY_EVENTS = ['mousemove', 'pointermove', 'mousedown', 'wheel']; 
  
  // Check if user has already verified password in this session
  // const [sessionVerified, setSessionVerified] = useState(() => {
  //   // Check sessionStorage on component mount
  //   return sessionStorage.getItem('walletVerified') === 'true';
  // });

  // Inactivity timeout states
  // const [showInactivityModal, setShowInactivityModal] = useState(false);
  // const INACTIVITY_TIMEOUT = 300000; // 5 minutes in milliseconds

  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  // const location = useLocation(); // Commented out - not used after password code removal
  const { translations: t } = useLanguage();
  const { currentUser, userProfile: authUserProfile } = useAuth();

  // Build public ViewProfile URL for sharing (QR + copy link)
  // Simple format: https://iwinto.com/@username
  const profileUrl = (() => {
    if (!currentUser?.id || !authUserProfile) return '';

    const usernameSlug =
      authUserProfile.username ||
      authUserProfile.display_name ||
      (currentUser.user_metadata?.full_name || currentUser.user_metadata?.name) ||
      'user';

    const base =
      (process.env.REACT_APP_SHARE_BASE_URL &&
        process.env.REACT_APP_SHARE_BASE_URL.replace(/\/$/, '')) ||
      (typeof window !== 'undefined'
        ? window.location.origin.replace(/\/$/, '')
        : 'https://iwinto.com');

    // Simple format: /@username → handled by ViewProfile route
    return `${base}/@${encodeURIComponent(usernameSlug)}`;
  })();

  // ---------- Helpers ----------
  const getFlagImage = (isoOrCode) => {
    const code = (isoOrCode || '').toUpperCase();
    const flagMap = {
      AE: aeFlag,
      AED: aeFlag, // UAE Dirham - use UAE flag
      US: usFlag,
      USD: usFlag,
      FR: frFlag,
      EUR: frFlag, // Euro - use French flag
      SA: saudiFlag,
      SAR: saudiFlag, // Saudi Riyal
      SY: syriaFlag,
      SYP: syriaFlag, // Syrian Pound
      QA: qaFlag,
      QAR: qaFlag, // Qatari Riyal
      LB: lbFlag,
      LBP: lbFlag // Lebanese Pound
    };
    return flagMap[code] || usFlag;
  };

  const formatNumber = (n, digits = 2) => {
    if (n == null || Number.isNaN(n)) return '0.00';
    return Number(n).toLocaleString('en-US', {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits
    });
  };

  // ========== COMMENTED OUT: Unused helper function ==========
  // const getUsdToCurrencyRate = (c) => {
  //   if (!c) return 1;
  //   return (
  //     c.usdToCurrency ??
  //     c.USDToCurrency ??
  //     c.usd_to_currency ??
  //     c.rate ??
  //     c.exchangeRate ??
  //     c.usd_rate ??
  //     c.USDToLocal ??
  //     c.USDToCoinExchangeRate ??
  //     1
  //   );
  // };

  // ========== COMMENTED OUT: Unused conversion function ==========
  // const winiToUsd = (wini) => {
  //   const rate = winiToUsdRate || 12; // Use 12 USD per winicoin as default
  //   return Number(wini) * Number(rate);
  // };

  // Convert Wini tokens -> selected currency (similar to CurrencyConverter.jsx)
  // First convert winicoins to USD, then convert USD to selected currency
  const convertTokensToCurrency = (tokens, currency) => {
    if (!currency || !winiToUsdRate) return 0;
    // Convert winicoins to USD
    const usdAmount = tokens * winiToUsdRate;
    // Convert USD to selected currency (currency.rate is how many local units per 1 USD)
    const result = usdAmount * currency.rate;
    return result;
  };

  // ========== COMMENTED OUT: Password Protection Functions ==========
  // Note: wallet_password column doesn't exist in profiles table yet
  // Using localStorage as temporary storage until column is added
  // const checkWalletPassword = async (userId) => {
  //   try {
  //     // First try to get from localStorage (temporary solution)
  //     const storedPassword = localStorage.getItem(`wallet_password_${userId}`);
  //     if (storedPassword) {
  //       return storedPassword;
  //     }

  //     // Try to fetch from Supabase profiles table (if column exists)
  //     try {
  //       const { data, error } = await supabase
  //         .from('profiles')
  //         .select('wallet_password')
  //         .eq('user_id', userId)
  //         .single();
        
  //       if (!error && data && data.wallet_password) {
  //         // Cache in localStorage for faster access
  //         localStorage.setItem(`wallet_password_${userId}`, data.wallet_password);
  //         return data.wallet_password;
  //       }
  //     } catch (supabaseError) {
  //       // Column doesn't exist or other error - fall back to localStorage
  //       console.log('[Wallet] wallet_password column not available, using localStorage');
  //     }

  //     return null;
  //   } catch (error) {
  //     console.error('Error checking wallet password:', error);
  //     return null;
  //   }
  // };

  // const setupWalletPassword = async (userId, newPassword) => {
  //   try {
  //     // Store in localStorage immediately
  //     localStorage.setItem(`wallet_password_${userId}`, newPassword);

  //     // Try to update in Supabase profiles table (if column exists)
  //     try {
  //       const { error } = await supabase
  //         .from('profiles')
  //         .update({ wallet_password: newPassword })
  //         .eq('user_id', userId);
        
  //       if (error) {
  //         // Column might not exist - that's okay, we have localStorage
  //         console.log('[Wallet] wallet_password column not available, using localStorage only');
  //       }
  //     } catch (supabaseError) {
  //       // Column doesn't exist - that's okay, we have localStorage
  //       console.log('[Wallet] wallet_password column not available, using localStorage only');
  //     }

  //     return true;
  //   } catch (error) {
  //     console.error('Error setting up wallet password:', error);
  //     return false;
  //   }
  // };

  // const verifyWalletPassword = (storedPassword, inputPassword) => {
  //   return storedPassword === inputPassword;
  // };

  // const handlePasswordSubmit = async () => {
  //   setPasswordError('');

  //   if (isPasswordSetup) {
  //     // Password setup mode
  //     if (password.length !== 4) {
  //       setPasswordError(t.wallet?.password?.errors?.length || "Password must be exactly 4 digits");
  //       return;
  //     }
  //     if (!/^\d+$/.test(password)) {
  //       setPasswordError(t.wallet?.password?.errors?.digitsOnly || "Password must contain only digits");
  //       return;
  //     }
  //     if (password !== confirmPassword) {
  //       setPasswordError(t.wallet?.password?.errors?.mismatch || "Passwords do not match");
  //       return;
  //     }

  //     const success = currentUser ? await setupWalletPassword(currentUser.id, password) : false;
  //     if (success) {
  //       setIsPasswordVerified(true);
  //       setSessionVerified(true);
  //       sessionStorage.setItem('walletVerified', 'true');
  //       setShowPasswordModal(false);
  //       setPassword('');
  //       setConfirmPassword('');
  //     } else {
  //       setPasswordError(t.wallet?.password?.errors?.setupFailed || "Failed to set up password. Please try again.");
  //     }
  //   } else {
  //     // Password verification mode
  //     if (password.length !== 4) {
  //       setPasswordError(t.wallet?.password?.errors?.length || "Password must be exactly 4 digits");
  //       return;
  //     }

  //     const storedPassword = currentUser ? await checkWalletPassword(currentUser.id) : null;
  //     if (storedPassword && verifyWalletPassword(storedPassword, password)) {
  //       setIsPasswordVerified(true);
  //       setSessionVerified(true);
  //       sessionStorage.setItem('walletVerified', 'true');
  //       setShowPasswordModal(false);
  //       setPassword('');
  //     } else {
  //       setPasswordError(t.wallet?.password?.errors?.incorrect || "Incorrect password. Please try again.");
  //     }
  //   }
  // };

  // const handlePasswordKeyPress = (e) => {
  //   if (e.key === 'Enter') {
  //     handlePasswordSubmit();
  //   }
  // };

  // ---------- COMMENTED OUT: Inactivity Timeout Functions ----------
  // const clearInactivityTimer = () => {
  //   if (inactivityTimerRef.current) {
  //     clearTimeout(inactivityTimerRef.current);
  //     inactivityTimerRef.current = null;
  //   }
  // };

  // const startInactivityTimer = () => {
  //   clearInactivityTimer();
  //   inactivityTimerRef.current = setTimeout(() => {
  //     setShowInactivityModal(true);
  //   }, INACTIVITY_TIMEOUT);
  // };

  // // Only reset if user is verified and no modal is showing
  // const handleUserActivity = () => {
  //   if (sessionVerified && !showPasswordModal && !showInactivityModal) {
  //     startInactivityTimer();
  //   }
  // };

  // const handleInactivityAcknowledge = () => {
  //   sessionStorage.removeItem('walletVerified');
  //   setSessionVerified(false);
  //   setIsPasswordVerified(false);
  //   setShowInactivityModal(false);
  //   clearInactivityTimer();
  //   setShowPasswordModal(true);
  // };


  // ---------- Supabase fetchers ----------
  const fetchCurrencies = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('currencies')
        .select('*')
        .order('iso');

      if (error) {
        console.error('Error fetching currencies:', error);
        setCurrencies([]);
        return [];
      }

      const list = (data || []).map((currency) => ({
        id: currency.iso,
        code: currency.iso,
        iso: currency.iso,
        name: currency.name || currency.iso,
        rate: currency.rate || 1,
        USDToCoinExchangeRate: currency.rate || 1, // For compatibility with existing code
        ...currency
      }));

      setCurrencies(list);
      return list;
    } catch (error) {
      console.error('Error fetching currencies:', error);
      setCurrencies([]);
      return [];
    }
  }, []);

  const fetchWinicoinRate = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('app_settings')
        .select('wini_to_usd_rate')
        .limit(1)
        .single();

      if (error) {
        console.error('Error fetching Wini<->USD rate:', error);
        // Use default rate of 12 USD per winicoin
        setWiniToUsdRate(12);
        return;
      }

      // Use fetched rate or default to 12 USD per winicoin
      setWiniToUsdRate(data?.wini_to_usd_rate || 12);
    } catch (e) {
      console.error('Error fetching Wini<->USD rate:', e);
      // Use default rate of 12 USD per winicoin
      setWiniToUsdRate(12);
    }
  }, []);

  // Helper function to decode bytea field from Supabase
  // Defined early so it can be used in other functions
  const decodeBytea = useCallback((byteaValue) => {
    if (!byteaValue) return 0;
    
    // If it's already a number, return it
    if (typeof byteaValue === 'number') {
      return byteaValue;
    }
    
    // If it's a string, try to parse it
    if (typeof byteaValue === 'string') {
      // Remove hex prefix if present (\x...)
      const cleaned = byteaValue.replace(/^\\x/, '').replace(/^0x/, '');
      // Try parsing as number directly
      const parsed = parseFloat(byteaValue);
      if (!Number.isNaN(parsed)) {
        return parsed;
      }
      // Try parsing hex string
      try {
        const hexParsed = parseInt(cleaned, 16);
        if (!Number.isNaN(hexParsed)) {
          return hexParsed;
        }
      } catch {}
      return 0;
    }
    
    // If it's Uint8Array or Buffer, decode it
    if (byteaValue instanceof Uint8Array || (typeof Buffer !== 'undefined' && byteaValue instanceof Buffer)) {
      try {
        // Try to decode as text first
        const decoded = new TextDecoder().decode(byteaValue);
        const parsed = parseFloat(decoded);
        if (!Number.isNaN(parsed)) {
          return parsed;
        }
        // If text decode doesn't work, try interpreting as binary number
        // Convert bytes to number (little-endian)
        let num = 0;
        for (let i = 0; i < Math.min(byteaValue.length, 8); i++) {
          num += byteaValue[i] * Math.pow(256, i);
        }
        return num;
      } catch {
        return 0;
      }
    }
    
    return 0;
  }, []);

  const fetchWalletBalance = useCallback(async (userId) => {
    try {
      // First try to fetch from wallet_view if it exists (as a view might decode bytea)
      // Otherwise, fetch from wallet table
      let data = null;
      let error = null;

      // Try wallet_view first
      try {
        const viewResult = await supabase
          .from('wallet_view')
          .select('winicoins')
          .eq('user_id', userId)
          .single();
        
        if (!viewResult.error && viewResult.data) {
          data = viewResult.data;
        } else {
          // If wallet_view doesn't exist or fails, try wallet table
          const walletResult = await supabase
            .from('wallet')
            .select('winicoins')
            .eq('user_id', userId)
            .single();
          
          data = walletResult.data;
          error = walletResult.error;
        }
      } catch (viewError) {
        // wallet_view doesn't exist, use wallet table
        const walletResult = await supabase
          .from('wallet')
          .select('winicoins')
          .eq('user_id', userId)
          .single();
        
        data = walletResult.data;
        error = walletResult.error;
      }

      if (error) {
        console.error('Error fetching wallet balance:', error);
        setUserTokens(0);
        return;
      }

      if (data && data.winicoins !== null && data.winicoins !== undefined) {
        // Decode the bytea field
        const balance = decodeBytea(data.winicoins);
        setUserTokens(Number.isFinite(balance) && balance >= 0 ? balance : 0);
      } else {
        setUserTokens(0);
      }
    } catch (error) {
      console.error('Error fetching wallet balance:', error);
      setUserTokens(0);
    }
  }, [decodeBytea]);

  const fetchUserProfile = useCallback(async (userId, loadedCurrencies) => {
    try {
      const { data: profile, error } = await supabase
        .from('profiles')
        .select('display_name, username, user_id')
        .eq('user_id', userId)
        .single();

      if (error || !profile) {
        console.error('Error fetching user profile:', error);
        setUserProfile({ name: 'User', defaultCurrency: 'USD' });
        return;
      }

      // ========== COMMENTED OUT: Wallet password check ==========
      // const walletPassword = localStorage.getItem(`wallet_password_${userId}`) || null;

      const profileData = {
        name: profile.display_name || profile.username || 'User',
        defaultCurrency: 'USD', // Default currency
        // wallet_password: walletPassword
      };

      setUserProfile(profileData);

      // ========== COMMENTED OUT: Password verification logic ==========
      // const hasWalletPassword = walletPassword;
      // if (!hasWalletPassword) {
      //   // User needs to set up wallet password
      //   setIsPasswordSetup(true);
      //   setShowPasswordModal(true);
      // } else if (!sessionVerified) {
      //   // User needs to verify wallet password (only if not already verified in this session)
      //   setIsPasswordSetup(false);
      //   setShowPasswordModal(true);
      // }

      // Fetch wallet balance
      await fetchWalletBalance(userId);

      // Select default currency after currencies are loaded
      const list = loadedCurrencies || [];
      if (list.length > 0) {
        const defCode = (profileData.defaultCurrency || 'USD').toUpperCase();
        const byDefault = list.find((c) => (c.code || '').toUpperCase() === defCode);
        const usd = list.find((c) => (c.code || '').toUpperCase() === 'USD');
        setSelectedCurrency(byDefault || usd || list[0]);
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
    }
  }, [fetchWalletBalance]); // Removed 'currencies' dependency - we use loadedCurrencies parameter instead

  // Subscribe to the user's Wini balance in real-time
  useEffect(() => {
    if (!currentUser?.id) return;
    
    console.log('Setting up wallet listener for UID:', currentUser.id);

    const channel = supabase
      .channel('wallet-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'wallet',
          filter: `user_id=eq.${currentUser.id}`
        },
        (payload) => {
          console.log('Wallet change received:', payload);
          if (payload.new) {
            const winicoins = payload.new.winicoins;
            // Use the decodeBytea helper function
            const balance = decodeBytea(winicoins);
            setUserTokens(Number.isFinite(balance) && balance >= 0 ? balance : 0);
          }
        }
      )
      .subscribe();

    // Also fetch initial balance
    fetchWalletBalance(currentUser.id);

    return () => {
      supabase.removeChannel(channel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.id]); // Only depend on currentUser.id to prevent infinite loops

  // ========== COMMENTED OUT: Check if user is coming from wallet-related routes ==========
  // useEffect(() => {
  //   // Check if location state indicates coming from wallet-related route
  //   if (location.state?.fromWalletRoute) {
  //     setSessionVerified(true);
  //     sessionStorage.setItem('walletVerified', 'true');
  //   }
  // }, [location.state]);

  // ---------- Auth & initial boot ----------
  // Use a ref to track if we've already initialized to prevent multiple runs
  const hasInitialized = useRef(false);
  const initializedUserId = useRef(null);
  
  useEffect(() => {
    if (!currentUser?.id) {
      setLoading(false);
      hasInitialized.current = false;
      initializedUserId.current = null;
      return;
    }

    // Reset initialization if user changed
    if (initializedUserId.current !== currentUser.id) {
      hasInitialized.current = false;
      initializedUserId.current = null;
    }

    // Prevent multiple initializations for the same user
    if (hasInitialized.current) {
      return;
    }

    const init = async () => {
      try {
        hasInitialized.current = true;
        initializedUserId.current = currentUser.id;
        setLoading(true);
        await fetchWinicoinRate();
        const list = await fetchCurrencies();
        await fetchUserProfile(currentUser.id, list);
      } catch (error) {
        console.error('Error initializing wallet:', error);
        hasInitialized.current = false; // Reset on error so it can retry
        initializedUserId.current = null;
      } finally {
        setLoading(false);
      }
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.id]); // Only depend on currentUser.id to prevent infinite loops

  // Close currency dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (!dropdownRef.current) return;
      if (dropdownRef.current.contains(e.target)) return;
      setShowCurrencyDropdown(false);
    };
    if (showCurrencyDropdown) {
      document.addEventListener('mousedown', handler);
    }
    return () => document.removeEventListener('mousedown', handler);
  }, [showCurrencyDropdown]);

  // ========== COMMENTED OUT: Set up inactivity timer and activity listeners ==========
  // useEffect(() => {
  //   // Only watch inactivity when verified and no blocking modals
  //   if (!(sessionVerified && !showPasswordModal && !showInactivityModal)) {
  //     clearInactivityTimer();
  //     return;
  //   }

  //   // Start (or restart) the timer when the effect conditions are met
  //   startInactivityTimer();

  //   // Attach mouse-only listeners on the document
  //   const activityHandler = () => handleUserActivity();
  //   INACTIVITY_EVENTS.forEach(evt =>
  //     document.addEventListener(evt, activityHandler, { passive: true })
  //   );

  //   // Optionally: when tab regains visibility, treat it as activity
  //   const onVisible = () => {
  //     if (!document.hidden) handleUserActivity();
  //   };
  //   document.addEventListener('visibilitychange', onVisible);

  //   // Cleanup
  //   return () => {
  //     clearInactivityTimer();
  //     INACTIVITY_EVENTS.forEach(evt =>
  //       document.removeEventListener(evt, activityHandler)
  //     );
  //     document.removeEventListener('visibilitychange', onVisible);
  //   };
  // }, [sessionVerified, showPasswordModal, showInactivityModal]);


  const handleCurrencySelect = (currency) => {
    setSelectedCurrency(currency);
    setShowCurrencyDropdown(false);
  };

  const toggleBalanceVisibility = () => setShowBalance((v) => !v);

  // Navigation handlers
  const handleSend = () => {
    // ========== COMMENTED OUT: Password verification ==========
    // setSessionVerified(true);
    // sessionStorage.setItem('walletVerified', 'true');
    navigate('/send', { state: { fromWalletRoute: true } });
  };
  const handleDeposit = () => {
    // ========== COMMENTED OUT: Password verification ==========
    // setSessionVerified(true);
    // sessionStorage.setItem('walletVerified', 'true');
    navigate('/deposit', { state: { fromWalletRoute: true } });
  };
  const handleCard = () => {
    // ========== COMMENTED OUT: Password verification ==========
    // setSessionVerified(true);
    // sessionStorage.setItem('walletVerified', 'true');
    navigate('/card', { state: { fromWalletRoute: true } });
  };
  const handleQR = () => setShowShareModal(true);
  const handleTrans = () => {
    // ========== COMMENTED OUT: Password verification ==========
    // setSessionVerified(true);
    // sessionStorage.setItem('walletVerified', 'true');
    navigate('/trans', { state: { fromWalletRoute: true } });
  };
  const handleCurrencyConverter = () => navigate('/currency-converter');

  // ========== COMMENTED OUT: Debug function ==========
  // const debugWallet = async () => {
  //   if (!currentUser?.id) return;
  //   try {
  //     console.log('=== DEBUG WALLET ===');
  //     console.log('User ID:', currentUser.id);
  //     
  //     const { data: walletData, error: walletError } = await supabase
  //       .from('wallet')
  //       .select('*')
  //       .eq('user_id', currentUser.id)
  //       .single();
  //     
  //     console.log('Wallet exists:', !!walletData);
  //     if (walletData) {
  //       console.log('Wallet data:', walletData);
  //       console.log('Winicoins field:', walletData.winicoins, typeof walletData.winicoins);
  //     }
  //     if (walletError) {
  //       console.error('Wallet error:', walletError);
  //     }
  //     
  //     // Also check profile
  //     const { data: profileData, error: profileError } = await supabase
  //       .from('profiles')
  //       .select('display_name, username')
  //       .eq('user_id', currentUser.id)
  //       .single();
  //     
  //     console.log('Profile exists:', !!profileData);
  //     if (profileData) {
  //       console.log('Profile data:', profileData);
  //     }
  //     if (profileError) {
  //       console.error('Profile error:', profileError);
  //     }

  //     // Check localStorage wallet password
  //     // const storedPassword = localStorage.getItem(`wallet_password_${currentUser.id}`);
  //     // console.log('Wallet password (localStorage):', storedPassword ? 'Set' : 'Not set');
  //   } catch (error) {
  //     console.error('Debug error:', error);
  //   }
  // };

  // Display helpers
  const currencyAmount = convertTokensToCurrency(userTokens, selectedCurrency);
  // const masked = '•••••••'; // Commented out - not used

  if (loading) {
    return (
      <div className="wallet-page">
        <button
          className="pages-back-btn"
          onClick={() => navigate(-1)}
          aria-label="Back"
          title="Back"
        >
          <FiArrowLeft />
        </button>
        <div className="loading">Loading...</div>
      </div>
    );
  }

  // ========== COMMENTED OUT: Show inactivity modal ==========
  // if (showInactivityModal) {
  //   return (
  //     <div className="wallet-page">
  //       <button
  //         className="pages-back-btn"
  //         onClick={() => navigate(-1)}
  //         aria-label="Back"
  //         title="Back"
  //       >
  //         <FiArrowLeft />
  //       </button>
  //       <div className="inactivity-modal-overlay">
  //         <div className="inactivity-modal">
  //           <div className="inactivity-modal-header">
  //             <div className="inactivity-icon">
  //               <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
  //                 <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" fill="#ffffff"/>
  //               </svg>
  //             </div>
  //             <h2>{t.wallet?.session?.expiredTitle || "Session Expired"}</h2>
  //             <p>{t.wallet?.session?.expiredBody || "You have been inactive for too long. Returning you back to login."}</p>
  //           </div>
  //           
  //           <div className="inactivity-modal-footer">
  //             <button 
  //               className="inactivity-acknowledge-btn"
  //               onClick={handleInactivityAcknowledge}
  //             >
  //               {t.wallet?.session?.acknowledge || "I Understand"}
  //             </button>
  //           </div>
  //         </div>
  //       </div>
  //     </div>
  //   );
  // }

  // ========== COMMENTED OUT: Show password modal ==========
  // if (showPasswordModal && !isPasswordVerified && !sessionVerified) {
  //   return (
  //     <div className="wallet-page">
  //       <button
  //         className="pages-back-btn"
  //         onClick={() => navigate(-1)}
  //         aria-label="Back"
  //         title="Back"
  //       >
  //         <FiArrowLeft />
  //       </button>
  //       <div className="password-modal-overlay">
  //         <button 
  //           className="password-modal-back-btn"
  //           onClick={() => navigate(-1)}
  //           aria-label="Go back"
  //           title="Go back"
  //         >
  //           <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
  //             <path d="M19 12H5M12 19l-7-7 7-7" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  //           </svg>
  //         </button>
  //         
  //         <div className="password-modal">
  //           <div className="password-modal-header">
  //           <h2>
  //             {isPasswordSetup
  //               ? (t.wallet?.password?.setupTitle || "Set Up Wallet Password")
  //               : (t.wallet?.password?.enterTitle || "Enter Wallet Password")}
  //           </h2>
  //           <p>
  //             {isPasswordSetup
  //               ? (t.wallet?.password?.setupDesc || "Create a 4-digit password to protect your wallet")
  //               : (t.wallet?.password?.enterDesc || "Enter your 4-digit wallet password")}
  //           </p>            
  //           </div>   
  //           <div className="password-modal-body">
  //             <div className="password-input-group">
  //             <label htmlFor="password">
  //               {t.wallet?.password?.label || "Password (4 digits)"}
  //             </label>
  //               <input
  //                 id="password"
  //                 type="password"
  //                 value={password}
  //                 onChange={(e) => setPassword(e.target.value)}
  //                 onKeyPress={handlePasswordKeyPress}
  //                 placeholder={t.wallet?.password?.placeholder || "Enter 4-digit password"}
  //                 maxLength={4}
  //                 className="password-input"
  //               />
  //             </div>
  //             
  //             {isPasswordSetup && (
  //               <div className="password-input-group">
  //                 {t.wallet?.password?.confirmLabel || "Confirm Password"}
  //                 <input
  //                   id="confirmPassword"
  //                   type="password"
  //                   value={confirmPassword}
  //                   onChange={(e) => setConfirmPassword(e.target.value)}
  //                   onKeyPress={handlePasswordKeyPress}
  //                   placeholder={t.wallet?.password?.confirmPlaceholder || "Confirm 4-digit password"}
  //                   maxLength={4}
  //                   className="password-input"
  //                 />
  //               </div>
  //             )}
  //             
  //             {passwordError && (
  //               <div className="password-error">{passwordError}</div>
  //             )}
  //           </div>
  //           
  //           <div className="password-modal-footer">
  //           <button className="password-submit-btn" onClick={handlePasswordSubmit}>
  //             {isPasswordSetup
  //               ? (t.wallet?.password?.submitSetup || "Set Password")
  //               : (t.wallet?.password?.submitEnter || "Unlock Wallet")}
  //           </button>
  //           </div>
  //         </div>
  //       </div>
  //     </div>
  //   );
  // }

  return (
    <div className="wallet-page">
      <FloatingSidebarButton userProfile={userProfile} />
      <button
        className="pages-back-btn"
        onClick={() => navigate(-1)}
        aria-label="Back"
        title="Back"
      >
        <FiArrowLeft />
      </button>

      {showShareModal && (
        <ShareProfileModal
          onClose={() => setShowShareModal(false)}
          profileUrl={profileUrl}
        />
      )}

      <div className="wallet-container">
        {/* Header Bar */}
        <div className="wallet-header">
          <div className="user-name">{userProfile?.name || 'User'}</div>

          <div className="wallet-currency-selector" ref={dropdownRef}>
            <button
              className="wallet-currency-button"
              onClick={() => setShowCurrencyDropdown((s) => !s)}
              aria-haspopup="listbox"
              aria-expanded={showCurrencyDropdown}
            >
              <img
                src={getFlagImage(selectedCurrency?.iso || selectedCurrency?.code)}
                alt={selectedCurrency?.code || 'USD'}
                className="wallet-currency-flag"
              />
              <span className="wallet-currency-code">
                {selectedCurrency?.code || selectedCurrency?.name || 'USD'}
              </span>
              <i className="wallet-chevron-down">▼</i>
            </button>

            {showCurrencyDropdown && (
              <div className="wallet-currency-dropdown" role="listbox">
                {currencies.map((currency) => (
                  <div
                    key={currency.id}
                    className="wallet-currency-option"
                    role="option"
                    aria-selected={selectedCurrency?.code === currency.code}
                    onClick={() => handleCurrencySelect(currency)}
                  >
                    <img
                      src={getFlagImage(currency.iso || currency.code)}
                      alt={currency.code}
                      className="wallet-currency-flag"
                    />
                    <span>{currency.name || currency.code}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Balance Card */}
        <div className="balance-card">
          <div className="balance-header">
          {t.wallet?.balance?.title || "Total Balance"}
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              {/* <button 
                className="visibility-toggle" 
                onClick={debugWallet}
                style={{ fontSize: '12px', padding: '4px 8px', background: '#f0f0f0', border: '1px solid #ccc', borderRadius: '4px' }}
              >
                Debug
              </button> */}
              <button className="visibility-toggle" onClick={toggleBalanceVisibility}>
                <i className={`eye-icon ${showBalance ? 'eye' : 'eye-slash'}`}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    {showBalance ? (
                      <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" fill="#666666"/>
                    ) : (
                      <path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z" fill="#666666"/>
                    )}
                  </svg>
                </i>
              </button>
            </div>
          </div>

          {/* Amount in selected currency */}
          <div className="balance-amount">
            <span className="amount-text">
              {showBalance ? formatNumber(currencyAmount, 2) : '•••••••'}
            </span>
            <span className="currency-symbol">
              {selectedCurrency?.code || selectedCurrency?.name || 'USD'}
            </span>
          </div>

          {/* Wini amount (always in Wini units) */}
          <div className="token-amount">
            <span className="token-value">
              {showBalance ? formatNumber(userTokens, 2) : '•••••••'}
            </span>
            <span className="token-label"> Wini's</span>
          </div>

          <div
  className="exchange-wrap"
  onClick={handleCurrencyConverter}
  role="button"
  tabIndex={0}
  aria-label="Open currency converter"
  onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && handleCurrencyConverter()}
>
  <div className="exchange-icon">
    <svg width="25" height="25" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1.41 16.09V20h-2.67v-1.93c-1.71-.36-3.16-1.46-3.27-3.4h1.96c.1 1.05.82 1.87 2.65 1.87 1.96 0 2.4-.98 2.4-1.59 0-.83-.44-1.61-2.67-2.14-2.48-.6-4.18-1.62-4.18-3.67 0-1.72 1.39-2.84 3.11-3.21V4h2.67v1.95c1.86.45 2.79 1.86 2.85 3.39H14.3c-.05-1.11-.64-1.87-2.22-1.87-1.5 0-2.4.68-2.4 1.64 0 .84.65 1.39 2.67 1.91s4.18 1.39 4.18 3.91c-.01 1.83-1.38 2.83-3.12 3.16z"
            fill="#1976D2"/>
    </svg>
  </div>
  {/* change the text to "Currency converter" if you prefer */}
  <span className="exchange-tooltip">
    {t.wallet?.converter?.tooltip || "Currency Converter"}
  </span>
</div>
        </div>

        {/* Action Buttons */}
        {/* Action Buttons */}
<div className="wallet-action-buttons-card">
  {/* Send */}
<div className="wallet-action-button" onClick={handleSend}>
  <div className="action-icon send">
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M3 7l9 5 9-5-9-4-9 4zM3 12l9 5 9-5M3 17l9 5 9-5"
            stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  </div>
  <span className="action-label">{t.wallet?.actions?.send || "Send"}</span>

</div>

{/* Deposit – perfect plus (not a cross) */}
<div className="wallet-action-button" onClick={handleDeposit}>
  <div className="action-icon deposit">
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 5v14M5 12h14"
            stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"/>
    </svg>
  </div>
  <span className="action-label">{t.wallet?.actions?.deposit || "Deposit"}</span>
</div>

{/* Card – light red bg, darker red icon */}
<div className="wallet-action-button" onClick={handleCard}>
  <div className="action-icon card">
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="3" y="5" width="18" height="14" rx="2.5"
            stroke="currentColor" strokeWidth="2"/>
      <path d="M3 10h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  </div>
  <span className="action-label">{t.wallet?.actions?.card || "Card"}</span>
</div>

{/* QR – clear 2×2 grid with rounded squares */}
{/* QR – fixed 2×2 rounded squares */}
<div className="wallet-action-button" onClick={handleQR}>
  <div className="action-icon qr">
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="5"  y="5"  width="6" height="6" rx="1.5" stroke="currentColor" strokeWidth="2"/>
      <rect x="13" y="5"  width="6" height="6" rx="1.5" stroke="currentColor" strokeWidth="2"/>
      <rect x="5"  y="13" width="6" height="6" rx="1.5" stroke="currentColor" strokeWidth="2"/>
      <rect x="13" y="13" width="6" height="6" rx="1.5" stroke="currentColor" strokeWidth="2"/>
    </svg>
  </div>
  <span className="action-label">{t.wallet?.actions?.qr || "QR"}</span>
</div>


{/* Trans. – two arrows with visible gap */}
{/* Trans. – stacked arrows */}
<div className="wallet-action-button" onClick={handleTrans}>
  <div className="action-icon trans">
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      {/* Top arrow → */}
      <path d="M6 8h12m0 0-3-3m3 3-3 3"
            stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      {/* Bottom arrow ← */}
      <path d="M18 16H6m0 0 3-3m-3 3 3 3"
            stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  </div>
  <span className="action-label">{t.wallet?.actions?.trans || "Trans."}</span>
</div>


</div>

      </div>
    </div>
  );
};

export default Wallet;